import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Minus, Plus, Wind, Fan, Shirt, Truck, Check, MapPin, Star, X, Droplet, Flower2, Leaf } from "lucide-react";
import { api, eur } from "@/lib/api";

const STD = { base: 2.99, minKg: 5, dryer: 0.39, folding: 0.69, ironFlat: 20, softener: 0.19, perfume: 0.19, sensitive: 0.19, pieces: [["T-Shirt", 0.99], ["Hemd / Bluse", 1.99], ["Hose", 1.99]], transport: 8.9, freeKm: 2, kmOneWay: 1, kmBoth: 2 };
const MODES = [["none", "Selbst bringen & abholen"], ["pickup", "Nur Abholung"], ["return", "Nur Rücklieferung"], ["both", "Abholung & Rücklieferung"]];
const CONSUMABLES = [["softener", "Weichspüler", STD.softener, Droplet], ["perfume", "Wäscheparfum", STD.perfume, Flower2], ["sensitive", "Sensitiv-Waschmittel", STD.sensitive, Leaf]];
const oneWay = (fee) => Math.round(fee * 55) / 100;

// Load live prices from the backoffice config into STD so every screen (Landing offers, Pricing)
// reflects the current prices. Returns the current price snapshot for display.
export async function loadPrices() {
  try {
    const { data: p } = await api.get("/prices");
    Object.assign(STD, { base: p.base_per_kg, minKg: p.min_kg, dryer: p.dryer_per_kg, folding: p.folding_per_kg, ironFlat: p.iron_flat_price, softener: p.consumable_per_kg, perfume: p.consumable_per_kg, sensitive: p.consumable_per_kg, pieces: [["T-Shirt", p.iron_tshirt_price], ["Hemd / Bluse", p.iron_shirt_price], ["Hose", p.iron_pants_price]], transport: p.transport_fee, freeKm: p.transport_free_km, kmOneWay: p.km_rate_one_way, kmBoth: p.km_rate_both });
    CONSUMABLES.forEach((c) => { c[2] = p.consumable_per_kg; });
  } catch (e) { /* keep defaults */ }
  return { base: STD.base, minKg: STD.minKg, folding: STD.folding, dryer: STD.dryer, ironFlat: STD.ironFlat, transport: STD.transport, freeKm: STD.freeKm, kmOneWay: STD.kmOneWay, kmBoth: STD.kmBoth };
}

// Hook: lädt die Live-Preise aus dem Backoffice und gibt den aktuellen Snapshot zurück (null bis geladen).
export function usePrices() {
  const [p, setP] = useState(null);
  useEffect(() => { loadPrices().then(setP); }, []);
  return p;
}

export function calcStandard({ kg, dryer, folding, ironFlat, pieces, softener, perfume, sensitive, transport, km }) {
  const effKg = Math.max(kg, STD.minKg);
  const lines = [{ key: "base", label: `${kg} kg × ${eur(STD.base)}`, total: effKg * STD.base, note: kg < STD.minKg ? `Mindestpreis bis ${STD.minKg} kg` : "" }];
  if (dryer) lines.push({ key: "dryer", label: "Trockner", total: kg * STD.dryer });
  if (folding) lines.push({ key: "folding", label: "Falten", total: kg * STD.folding });
  if (ironFlat) lines.push({ key: "iron_flat", label: "Bügel-Pauschale (alles)", total: STD.ironFlat });
  else {
    const n = (pieces || []).reduce((a, b) => a + b, 0);
    if (n > 0) lines.push({ key: "ironing", label: `Bügeln (${n} Stück)`, total: pieces.reduce((s, q, i) => s + q * STD.pieces[i][1], 0) });
  }
  if (softener) lines.push({ key: "softener", label: "Weichspüler", total: kg * STD.softener });
  if (perfume) lines.push({ key: "perfume", label: "Wäscheparfum", total: kg * STD.perfume });
  if (sensitive) lines.push({ key: "sensitive", label: "Sensitiv-Waschmittel", total: kg * STD.sensitive });
  if (transport !== "none") {
    lines.push({ key: "transport", label: MODES.find((m) => m[0] === transport)[1], total: transport === "both" ? STD.transport : oneWay(STD.transport) });
    const extraKm = Math.max(0, Math.ceil((km - STD.freeKm) * 2 - 1e-9) / 2);
    if (extraKm > 0) lines.push({ key: "distance", label: "Distanzzuschlag", total: extraKm * (transport === "both" ? STD.kmBoth : STD.kmOneWay) });
  }
  return { lines, total: lines.reduce((s, l) => s + l.total, 0) };
}

const transportAllowed = (off, mode) => {
  if (!off || mode === "none") return true;
  if (mode === "pickup") return !!off.pickup;
  if (mode === "return") return !!off.return;
  if (mode === "both") return !!(off.pickup && off.return);
  return false;
};

function Stepper({ value, onChange, min = 0, max = 99, unit, testid }) {
  return (
    <div className="inline-flex items-center rounded-xl border border-line bg-white h-12" data-testid={testid}>
      <button type="button" className="h-full px-3 w-muted hover:text-ink" onClick={() => onChange(Math.max(min, value - 1))} aria-label="weniger" data-testid={`${testid}-minus`}><Minus size={16} /></button>
      <span className="min-w-[56px] text-center font-semibold tabular-nums" data-testid={`${testid}-value`}>{value}{unit && <span className="w-muted text-sm font-normal ml-1">{unit}</span>}</span>
      <button type="button" className="h-full px-3 w-muted hover:text-ink" onClick={() => onChange(Math.min(max, value + 1))} aria-label="mehr" data-testid={`${testid}-plus`}><Plus size={16} /></button>
    </div>
  );
}

function Option({ active, onClick, icon: Icon, title, sub, price, testid, disabled }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={testid} aria-pressed={active}
      className={`w-full text-left rounded-2xl border p-4 flex items-center gap-3 transition-colors ${active ? "border-mint bg-mint-soft" : "border-line bg-white hover:border-gray-300"} disabled:opacity-60`}>
      {Icon && <span className={`h-10 w-10 shrink-0 grid place-items-center rounded-xl ${active ? "bg-mint text-mint-on" : "bg-gray-100 text-ink-2"}`}><Icon size={18} aria-hidden="true" /></span>}
      <span className="flex-1 min-w-0"><span className="block font-semibold">{title}</span>{sub && <span className="block text-sm w-muted">{sub}</span>}</span>
      <span className="text-sm font-semibold whitespace-nowrap">{price}</span>
      <span className={`h-6 w-6 shrink-0 rounded-full border grid place-items-center ${active ? "bg-mint border-mint text-mint-on" : "border-gray-300"}`}>{active && <Check size={14} />}</span>
    </button>
  );
}

const Card = ({ title, sub, children }) => (
  <section className="w-card p-5 space-y-4"><div><h2 className="font-bold text-lg">{title}</h2>{sub && <p className="w-muted text-sm">{sub}</p>}</div>{children}</section>
);

function ProviderPicker({ provider, setProvider, plz, setPlz }) {
  const [results, setResults] = useState(null);
  useEffect(() => {
    if (!/^\d{5}$/.test(plz)) { setResults(null); return; }
    api.get("/providers/search", { params: { plz, radius: 25 } }).then((r) => setResults(r.data.results)).catch(() => setResults([]));
  }, [plz]);
  return (
    <Card title="Anbieter wählen" sub="Optional: PLZ eingeben und einen Waschhelden auswählen – dann siehst du nur dessen angebotene Leistungen. Der kg-Preis ist überall gleich.">
      <div className="flex flex-col sm:flex-row gap-3">
        <label className="relative flex-1"><span className="sr-only">PLZ</span><MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-2" size={18} aria-hidden="true" />
          <input className="w-input !pl-11" inputMode="numeric" maxLength={5} placeholder="Deine Postleitzahl" value={plz} onChange={(e) => setPlz(e.target.value.replace(/\D/g, ""))} data-testid="calc-plz-input" /></label>
        {provider && <button type="button" className="w-btn w-btn-secondary" onClick={() => setProvider(null)} data-testid="calc-provider-clear"><X size={16} aria-hidden="true" /> Zurücksetzen</button>}
      </div>
      {provider && (
        <div className="rounded-2xl border border-mint bg-mint-soft p-4 flex items-center justify-between gap-3" data-testid="calc-provider-active">
          <div><p className="font-bold">{provider.display_name}</p><p className="text-sm w-muted">{provider.city}{provider.distance_km != null ? ` · ca. ${provider.distance_km} km` : ""}</p></div>
          <span className="w-chip bg-mint text-mint-on"><Star size={14} aria-hidden="true" /> {Number(provider.rating || 0).toFixed(1)}</span>
        </div>
      )}
      {results && !provider && (
        results.length ? (
          <ul className="grid sm:grid-cols-2 gap-2" data-testid="calc-provider-list">
            {results.slice(0, 6).map((r) => (
              <li key={r.id}><button type="button" onClick={() => setProvider(r)} data-testid={`calc-provider-${r.id}`} className="w-full text-left rounded-2xl border border-line bg-white p-3 hover:border-mint transition-colors">
                <span className="font-semibold block">{r.display_name}</span><span className="text-sm w-muted">ca. {r.distance_km} km · ★ {Number(r.rating || 0).toFixed(1)}</span>
              </button></li>
            ))}
          </ul>
        ) : <p className="w-muted text-sm">Keine Waschhelden im Umkreis gefunden – wir rechnen mit den Standardpreisen.</p>
      )}
    </Card>
  );
}

export default function Pricing() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [provider, setProvider] = useState(null);
  const [plz, setPlz] = useState(params.get("plz") || "");
  const [s, setS] = useState(() => {
    const has = ["kg", "dryer", "folding", "ironFlat", "transport"].some((k) => params.get(k) != null);
    const base = { kg: 5, dryer: false, folding: false, ironFlat: false, pieces: [0, 0, 0], softener: false, perfume: false, sensitive: false, transport: "none", km: 0 };
    if (!has) return base;
    const b = (k) => params.get(k) === "1";
    return { ...base, kg: Number(params.get("kg")) || 5, dryer: b("dryer"), folding: b("folding"), ironFlat: b("ironFlat"), transport: params.get("transport") || "both" };
  });
  const [quote, setQuote] = useState(null);
  const [priceTick, setPriceTick] = useState(0);
  const up = (patch) => setS((x) => ({ ...x, ...patch }));

  useEffect(() => {
    loadPrices().then(() => setPriceTick((t) => t + 1));
  }, []);

  useEffect(() => {
    const pid = params.get("provider");
    if (pid) api.get(`/providers/${pid}`).then((r) => setProvider(r.data)).catch(() => {});
  }, [params]);

  const svc = provider?.services;
  const avail = (k) => !provider || !!svc?.[k];
  const extras = useMemo(() => ({ dryer: s.dryer, folding: s.folding, iron_flat: s.ironFlat, ironing_pieces: s.pieces.reduce((a, b) => a + b, 0), pieces: s.pieces, softener: s.softener, perfume: s.perfume, sensitive: s.sensitive, transport: s.transport }), [s]);
  useEffect(() => {
    if (!provider) { setQuote(null); return; }
    const h = setTimeout(() => api.post("/quote", { provider_id: provider.id, kg: s.kg, extras, plz: /^\d{5}$/.test(plz) ? plz : null }).then((r) => setQuote(r.data)).catch(() => {}), 150);
    return () => clearTimeout(h);
  }, [provider, s.kg, extras, plz]);
  useEffect(() => {
    if (!provider) up({ km: 0 });
    else if (quote?.distance_km != null) up({ km: quote.distance_km });
  }, [provider, quote?.distance_km]);
  useEffect(() => {
    if (!provider) return;
    const o = provider.services || {};
    up({ dryer: s.dryer && o.dryer, folding: s.folding && o.folding, ironFlat: s.ironFlat && o.ironing, pieces: o.ironing ? s.pieces : [0, 0, 0], softener: s.softener && o.softener, perfume: s.perfume && o.perfume, sensitive: s.sensitive && o.sensitive, transport: transportAllowed(o, s.transport) ? s.transport : "none" });
  }, [provider?.id]);

  const price = useMemo(() => calcStandard(s), [s, priceTick]);
  const transportOn = s.transport !== "none";
  const ironFlatPrice = STD.ironFlat;
  const cfg = { transport_free_km: STD.freeKm, km_rate_one_way: STD.kmOneWay, km_rate_both: STD.kmBoth };

  const book = () => {
    const draft = { kg: s.kg, extras, self_pickup: s.transport === "none", address: { street: "", plz: /^\d{5}$/.test(plz) ? plz : "", city: "", notes: "" } };
    if (provider) { sessionStorage.setItem(`washly_draft_${provider.id}`, JSON.stringify(draft)); nav(`/book/${provider.id}?step=3`); return; }
    sessionStorage.setItem("washly_draft_calc", JSON.stringify(draft));
    nav(/^\d{5}$/.test(plz) ? `/search?plz=${plz}` : "/search");
  };

  return (
    <div className="w-container py-10">
      <p className="w-label !text-mint-ink">Preise</p>
      <h1 className="text-4xl sm:text-5xl font-extrabold mb-3 w-headline" data-testid="pricing-title">Transparent kalkulieren</h1>
      <p className="w-muted text-lg max-w-2xl mb-8">Die Grundleistung kostet überall <strong>{eur(STD.base)} je kg</strong> – der Preis ist bei allen Waschhelden gleich. Bis {STD.minKg} kg mindestens {eur(STD.minKg * STD.base)}. {provider ? <>Du siehst die Leistungen, die <strong>{provider.display_name}</strong> anbietet.</> : "Alle Zusatzleistungen sind optional."}</p>

      <div className="grid lg:grid-cols-12 gap-6 items-start">
        <div className="lg:col-span-7 space-y-4">
          <ProviderPicker provider={provider} setProvider={setProvider} plz={plz} setPlz={setPlz} />

          <Card title="Wäschemenge" sub="Schätzwert vor der Buchung — das verbindliche Gewicht wird bei der Übergabe gemessen.">
            <Stepper value={s.kg} onChange={(kg) => up({ kg })} min={1} max={30} unit="kg" testid="calc-kg" />
            {!provider && <p className="w-muted text-sm">Mindestpreis bis {STD.minKg} kg: {eur(STD.minKg * STD.base)}</p>}
          </Card>

          <Card title="Trocknung">
            <div className="grid sm:grid-cols-2 gap-3">
              <Option active={!s.dryer} onClick={() => up({ dryer: false })} icon={Wind} title="Lufttrocknen" sub="im Basispreis enthalten" price="inkl." testid="calc-dry-air" />
              <Option active={s.dryer} onClick={() => up({ dryer: true })} icon={Fan} title="Trockner" sub={avail("dryer") ? `+${eur(STD.dryer)} je kg` : "nicht angeboten"} price={avail("dryer") ? `+ ${eur(s.kg * STD.dryer)}` : ""} disabled={!avail("dryer")} testid="calc-dry-machine" />
            </div>
          </Card>

          <Card title="Zusatzleistungen">
            <Option active={s.folding} onClick={() => up({ folding: !s.folding })} title="Falten" sub={avail("folding") ? `+${eur(STD.folding)} je kg — sauber sortiert nach Kleidungsgruppen` : "nicht angeboten"} price={avail("folding") ? `+ ${eur(s.kg * STD.folding)}` : ""} disabled={!avail("folding")} testid="calc-folding" />
            <Option active={s.ironFlat} onClick={() => up({ ironFlat: !s.ironFlat })} icon={Shirt} title="Bügel-Pauschale" sub={avail("ironing") ? "Alle Sachen zusammen bügeln – Festpreis" : "nicht angeboten"} price={avail("ironing") ? `+ ${eur(ironFlatPrice)}` : ""} disabled={!avail("ironing")} testid="calc-iron-flat" />
            {avail("ironing") && (
              <div className={`rounded-2xl border border-line p-4 ${s.ironFlat ? "opacity-50" : ""}`}>
                <p className="font-semibold flex items-center gap-2 mb-3"><Shirt size={16} aria-hidden="true" /> Bügeln (je Stück) <span className="w-muted text-sm font-normal">— oder einzeln</span></p>
                <div className="space-y-3">
                  {STD.pieces.map(([name, pr], i) => (
                    <div key={name} className="flex items-center justify-between gap-4">
                      <span className="text-sm"><span className="block font-semibold">{name}</span><span className="w-muted">{eur(pr)}/Stück</span></span>
                      <Stepper value={s.pieces[i]} onChange={(v) => { const pieces = [...s.pieces]; pieces[i] = v; up({ pieces }); }} max={100} testid={`calc-piece-${i}`} />
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="space-y-3 pt-1" data-testid="calc-consumables">
              {CONSUMABLES.map(([k, label, rate, Icon]) => (
                <Option key={k} active={s[k]} onClick={() => up({ [k]: !s[k] })} icon={Icon} title={label} sub={avail(k) ? `+${eur(rate)} je kg — je nach Menge` : "nicht angeboten"} price={avail(k) ? `+ ${eur(s.kg * rate)}` : ""} disabled={!avail(k)} testid={`calc-${k}`} />
              ))}
            </div>
          </Card>

          <Card title="Transport">
            <div className="grid sm:grid-cols-2 gap-3">
              {MODES.map(([key, title]) => {
                const ok = transportAllowed(svc, key);
                const fee = key === "none" ? 0 : key === "both" ? STD.transport : oneWay(STD.transport);
                return <Option key={key} active={s.transport === key} onClick={() => up({ transport: key })} icon={Truck} title={title} sub={!ok ? "nicht angeboten" : fee ? `+${eur(fee)}` : "0 €"} price="" disabled={!ok} testid={`calc-transport-${key}`} />;
              })}
            </div>
            <div className={transportOn ? "" : "opacity-50"}>
              {provider ? (
                <p className="text-sm" data-testid="calc-distance-info">{quote?.distance_km != null ? <>Entfernung zu {provider.display_name}: <strong>{String(quote.distance_km).replace(".", ",")} km</strong></> : "Gib oben deine PLZ ein, damit wir die Entfernung berechnen."}</p>
              ) : (<>
                <label className="block font-semibold mb-2" htmlFor="calc-km">Entfernung: {s.km.toFixed(1).replace(".", ",")} km</label>
                <input id="calc-km" type="range" min={0} max={15} step={0.1} value={s.km} disabled={!transportOn} onChange={(e) => up({ km: Number(e.target.value) })} className="w-full accent-[var(--w-mint)]" data-testid="calc-km" />
              </>)}
              <p className="w-muted text-sm mt-2">Bis {cfg.transport_free_km} km inklusive. Danach +{cfg.km_rate_one_way} €/km (ein Weg) bzw. +{cfg.km_rate_both} €/km (kombiniert), auf 0,5 km aufgerundet.</p>
            </div>
          </Card>
        </div>

        <aside className="lg:col-span-5 lg:sticky lg:top-24 space-y-3">
          <div className="w-card p-5" data-testid="calc-summary">
            <p className="w-label">{provider ? `Preis bei ${provider.display_name}` : "Dein Preis"}</p>
            <ul className="space-y-2 mb-4">
              {price.lines.map((l) => (
                <li key={l.key} className="flex justify-between gap-4 text-sm" data-testid={`calc-line-${l.key}`}>
                  <span className="w-muted">{l.label}{l.note && <span className="block text-xs">{l.note}</span>}</span><span className="font-semibold tabular-nums">{eur(l.total)}</span>
                </li>
              ))}
            </ul>
            <div className="border-t border-line pt-4 flex items-end justify-between">
              <span className="font-bold">Gesamt</span>
              <span className="text-3xl font-extrabold tabular-nums w-headline" data-testid="calc-total">{eur(price.total)}</span>
            </div>
            <p className="w-muted text-xs mt-2">{provider ? "Verbindlich wird der Preis nach Annahme durch den Waschhelden und Gewichtsmessung bei der Übergabe." : `Einheitspreis: ${eur(STD.base)} je kg bei allen Waschhelden. Das verbindliche Gewicht wird bei der Übergabe gemessen.`}</p>
          </div>
          <button className="w-btn w-btn-primary w-full" onClick={book} data-testid="calc-book-btn">{provider ? `Bei ${provider.display_name} buchen` : "Mit diesen Optionen buchen"}</button>
        </aside>
      </div>
    </div>
  );
}
